/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import Stringify from '../stringify';

// Obecně všechny funkce vracející generátory nebo transformační funkce generátorů
// akceptují i obyčetný IterableIterator a vrací Generator.

// TODO: Co s volitelnými generickými parametry TReturn a TNext? Nechat obecné
// nebo rozepsat na void, undefined? Dát to všude nebo jen kde je potřeba?

/**
 * Transformační generátor, tj. funkce akceptující iterátor a vracející generátor.
 * @template T
 * @typedef {(iterator: IterableIterator<T>)
 *   => Generator<T, void, undefined>
 * } GeneratorTransformer
 */

/**
 * Vytvoří generátor, který transformuje iterátor pomocí sady transformačních generátorů.
 * @template T
 * @param {IterableIterator<T>} iterator
 * @param {...GeneratorTransformer<T>} generatorTransformers
 * @returns {Generator<T, void, undefined>}
 */
export function compose(iterator, ...generatorTransformers) {
  return generatorTransformers.reduce(
    (acc, generatorTransformer) => generatorTransformer(acc),
    fromIterator(iterator)
  );
}

/**
 * Získá první prvek iterátoru nebo `null`, pokud je ukončený.
 * @template T
 * @param {IterableIterator<T>} iterator
 * @returns {T | null}
 */
export function firstOrNull(iterator) {
  const next = iterator.next();
  return next.done ? null : next.value;
}

/**
 * Získá první prvek iterátoru nebo `undefined`, pokud je ukončený.
 * @template T
 * @param {IterableIterator<T>} iterator
 * @returns {T | undefined}
 */
export function firstOrUndefined(iterator) {
  const next = iterator.next();
  return next.done ? undefined : next.value;
}

/**
 * Vytvoří generátor z iterátoru.
 * @template T
 * @param {IterableIterator<T>} iterator
 * @generator
 */
export function* fromIterator(iterator) {
  yield* iterator;
}

/**
 * Vytvoří logující transformační generátor.
 *
 * Každou hodnotu zaloguje pomocí {@link logger} pomocí argumentů {@link name}
 * a `T#toString()`.
 * @template {import('../stringable').Stringable} T
 * @param {(...args: any[]) => void} logger
 * @param {string} name
 * @returns {GeneratorTransformer<T>}
 */
export function log(logger, name) {
  return function* (iterator) {
    for (const v of iterator) {
      logger(name, v.toString());
      yield v;
    }
  };
}

// TODO: Refaktorovat na datovou strukturu min-heap místo pole a řazení.
/**
 * Vytvoří generátor neklesajících hodnot, který spojuje iterátory neklesajících
 * hodnot a vybírá z nich vždy nejmenší prvek.
 * @template T
 * @param {(a: T, b: T) => number} compareFn Komparátor pro určení neklesající
 * monotonie hodnot, viz `compareFn` argument pro {@link Array.prototype.sort}.
 * @param  {...IterableIterator<T>} iterators Iterátory s neklesajícími hodnotami.
 *
 * Musí platit, že pro každé dvě po sobě jdoucí hodnoty `a` a `b` z iterátoru
 * vrací {@link compareFn}`(a, b)` nekladné hodnoty, a to pro všechny iterátory
 * z {@link iterators} jednotlivě.
 * @returns {Generator<T, void, undefined>} Generátor s neklesajícími hodnotami získaných
 * ze vstupních iterátorů.
 */
export function monotonicMerge(compareFn, ...iterators) {
  // TODO: Nemít komparátor naopak, aby se přidávalo/odebíralo na konec pole
  // generátorů pomocí pop a nemuselo se vše kopírovat při shift?
  /**
   * Komparační funkce hledání generátoru s nejmenším následující hodnotou
   * získanou pohledem do orákula. Jelikož pohled do orákula může vrátit done,
   * tak je nutné umět i tyto ukončené generátory správně řadit. Z hlediska
   * výkonu je vhodné řadit tyto hodnoty na začátek (za předpokladu, že v poli
   * generátorů níže se přistupuje k nejmenšímu prvku pomocí indexu 0), aby se
   * prázdné generátory co nejdříve odebraly a řadilo se co nejmenší pole.
   * Takové prvky by měly být infimum (nejmenší) dané relace, což splňuje
   * `-Infinity`. Pro stabilitu řazení se dvě infima musí považovat jako stejná.
   * @param {NextOracle<T>} a
   * @param {NextOracle<T>} b
   * @returns {number}
   */
  const nextOracleCompareFn = ({ nextOracle: a }, { nextOracle: b }) => {
    if (Boolean(a.done) === Boolean(b.done)) {
      return a.done ? 0 : compareFn(a.value, b.value);
    }
    return a.done ? -Infinity : Infinity;
  };

  // Pole iterátorů namapuje pomocí orákula, abychom mohli předvídat budoucnost
  // kvůli nutnosti řazení a hledání generátoru s nejmenším hodnotou.
  const generators = iterators.map(oracle);
  return (function* () {
    while (generators.length > 0) {
      // Po seřazení bude generátor s budoucím nejmenším hodnotou (nebo ukončený
      // generátor) na indexu 0 v poli generátorů.
      generators.sort(nextOracleCompareFn);
      // Získáme minimální hodnotu.
      const min = generators[0].next();
      if (min.done) {
        // Pokud je minimální generátor prázdný, tak ho vyjmeme z pole
        // generátorů, aby se zmenšila velikost řazeného pole.
        generators.shift();
      } else {
        // Jinak vrátíme tuto minimální hodnotu.
        yield min.value;
      }
    }
  })();
}

/**
 * Doplňující "rozhraní" s {@link nextOracle} getterem k `next` funkci
 * iterátorů/generátorů, které umožňuje předvídat výsledek volání `next`.
 * @template T
 * @typedef {{get nextOracle(): IteratorResult<T>}} NextOracle
 */

/**
 * Generátor s "orákulem", tj. getterem `nextOracle`, který umí předpovídat
 * výsledek dalšího volání `next`.
 * @template T
 * @template [TReturn = any]
 * @template [TNext = unknown]
 * @typedef {Generator<T, TReturn, TNext> & NextOracle<T>} OracleGenerator
 */

// Velmi volně inspirováno:
// - https://gitlab.com/svobol.com/iterator-has-next/-/blob/master/src/index.ts
/**
 * Vytvoří generátor s "orákulem" z iterátoru.
 * @template T
 * @template [TReturn = any]
 * @template [TNext = unknown]
 * @param {IterableIterator<T>} iterator
 * @returns {OracleGenerator<T, TReturn, TNext>}
 */
export function oracle(iterator) {
  let nextOracle = iterator.next();
  return /** @type {OracleGenerator<T, TReturn, TNext>} */ (
    Object.defineProperties(
      (function* () {
        while (!nextOracle.done) {
          const value = nextOracle.value;
          nextOracle = iterator.next();
          yield value;
        }
      })(),
      {
        nextOracle: { get: () => nextOracle },
        // TODO: Není obecné, nyní JS like serializace pro účely výpisů při ladění.
        // Předávat případně jako volitelný parametr?
        toString: { value: () => Stringify.value(nextOracle) }
      }
    )
  );
}
